// src/pages/supervisor/SupervisorDashboard.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Grid, 
    Paper, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Divider, 
    useTheme, 
    IconButton, 
    useMediaQuery,
    LinearProgress,
    Chip
} from '@mui/material';
import { 
    School, 
    Verified, 
    Category, 
    MilitaryTech, 
    Refresh as RefreshIcon, 
    Layers,
    CheckCircle,
    Domain
} from '@mui/icons-material';
import { apiService } from '../../apiServices/ApiService';
import { APISupervisorSpecialtyDashboard } from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%', borderRadius: 3, borderLeft: `6px solid ${color}`, boxShadow: 1 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: `${color}15`, borderRadius: '50%', color: color }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{title}</Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const SupervisorDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        total_university_specialties: 0,
        active_university_specialties: 0,
        total_my_specialties: 0,
        primary_specialties: 0,
        expert_level_specialties: 0
    });

    const supervisorId = getUserDetails("Id");
    const universityId = getUserDetails("universityId"); 

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            // Fetch stats using the dual-parameter layout: listDashboardData(API_ROUTE, "PARAM1/PARAM2")
            const response = await apiService.listDashboardData(
                APISupervisorSpecialtyDashboard,
                `${supervisorId}/${universityId}`
            );
            
            if (response.success && response.data) {
                setData(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch specialty metrics:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (supervisorId && universityId) {
            fetchDashboardData();
        }
    }, [supervisorId, universityId]);

    if (loading) {
        return <LoadingState message="Loading your dashboard statistics..." />;
    }

    return (
        <FullWidthContainer>
            <Box sx={{ backgroundColor: 'white', borderRadius: 3, mb: 3 }}>
                <SupervisorBar notificationCount={5} />

                {/* Welcome Header */}
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, color: theme.palette.primary.main }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Welcome back, {getUserDetails("userName") || 'Research Supervisor'}!
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Domain sx={{ fontSize: 16 }} /> {getUserDetails("university") || 'University'} • {getUserDetails("academicTitle") || 'Research Supervisor'}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                                size="small" 
                                onClick={fetchDashboardData} 
                                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                            >
                                <RefreshIcon size="small" />
                            </IconButton>
                            <Chip 
                                label="Portal Active" 
                                color="success" 
                                size="medium" 
                                variant="outlined" 
                                icon={<CheckCircle />} 
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Specialty Overview Statistics */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="My Specialties" 
                        value={data.total_my_specialties} 
                        icon={<Category />} 
                        color={theme.palette.primary.main} 
                        subtitle="Assigned specialty disciplines"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Primary Designations" 
                        value={data.primary_specialties} 
                        icon={<Verified />} 
                        color={theme.palette.success.main} 
                        subtitle="Main supervisory focus areas"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Expert Level Scope" 
                        value={data.expert_level_specialties} 
                        icon={<MilitaryTech />} 
                        color={theme.palette.warning.main} 
                        subtitle="Subject matter expert fields"
                    />
                </Grid>
            </Grid>

            {/* University & Specialty Context Details */}
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, fontWeight: 'bold' }}>
                    <School color="primary" /> University Specialty Framework
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={4} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Active specializations offered by your university:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" fontWeight="bold">
                                {data.active_university_specialties} / {data.total_university_specialties}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {data.total_university_specialties > 0 
                                    ? `${Math.round((data.active_university_specialties / data.total_university_specialties) * 100)}% active`
                                    : '0% active'}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={data.total_university_specialties > 0 
                                ? (data.active_university_specialties / data.total_university_specialties) * 100 
                                : 0
                            } 
                            sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ bgcolor: '#fdfae6', p: 2, borderRadius: 2, borderLeft: `4px solid ${theme.palette.warning.main}` }}>
                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 0.5 }}>
                                <Layers fontSize="small"/> Coverage Level
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                You are currently associated with <b>{data.total_my_specialties}</b> distinct academic specialty profiles in the system. Your expertise details assist in assigning matching research topics.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </FullWidthContainer>
    );
};

export default SupervisorDashboard;